import type { Route } from '@angular/router';
import { confirmExitGuard } from './confirm-exit.guard';

const title = (str: string) => `ALIX™ ${str} | IDEMIA`;

export type AppRouteConfig = Omit<Route, 'children'> & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  url?: (...args: any[]) => string;
  needAuth?: boolean;
  children?: AppRouteConfig[];
};

export const routes: Record<string, AppRouteConfig> = {
  home: {
    title: title('Home'),
    path: '',
    redirectTo: 'untagged-baggages/search/page/1',
    pathMatch: 'full',
    url: () => '/',
  },
  claims: {
    title: title('Cold searches'),
    path: 'claims',
    children: [
      {
        path: 'page/:page',
        url: (page: number) => `/claims/page/${page}`,
        loadComponent: () => import('./claims/components/claims.component').then((result) => result.ClaimsComponent),
      },
      {
        path: 'cold-search/:claimId/:claimTag',
        url: (id: string, tag: string) => `/claims/cold-search/${id}/${tag}`,
        loadComponent: () => import('./cold-search/components/cold-search.component').then(
          (result) => result.ColdSearchComponent,
        ),
      },
    ],
  },
  'tagged-baggages': {
    title: title('Conveyor luggage'),
    path: 'baggages',
    children: [
      {
        path: 'page/:page',
        url: (page: number) => `/baggages/page/${page}`,
        loadComponent: () => import('./tagged-baggages/components/tagged-baggages.component').then(
          (result) => result.TaggedBaggagesComponent,
        ),
      },
      {
        path: ':id',
        url: (id: string) => `/baggages/${id}`,
        loadComponent: () => import('./tagged-baggages/components/details/tagged-baggage.component').then(
          (result) => result.TaggedBaggageComponent,
        ),
      },
      {
        path: ':tbId/tagless-search',
        url: (id: string) => `/baggages/${id}/tagless-search`,
        loadComponent: () => import('./tagless-baggages/components/search/tagless-baggages-search.component').then(
          (result) => result.TaglessBaggagesSearchComponent,
        ),
      },
    ],
  },
  'untagged-baggages-search': {
    title: title('Hot searches'),
    path: 'untagged-baggages/search',
    children: [
      {
        path: 'page/:page',
        url: (page: number) => `/untagged-baggages/search/page/${page}`,
        loadComponent: () => import('./untagged-baggages/components/untagged-baggages.component')
          .then((result) => result.UntaggedBaggagesComponent),
      },
      {
        path: ':id',
        url: (id: string) => `/untagged-baggages/search/${id}`,
        loadComponent: () => import('./untagged-baggages/components/details/untagged-baggage.component').then(
          (result) => result.UntaggedBaggageComponent,
        ),
      },
      {
        path: 'hot-search/:ubId',
        url: (id: string) => `/untagged-baggages/search/hot-search/${id}`,
        loadComponent: () => import('./hot-search/components/hot-search.component').then(
          (result) => result.HotSearchComponent,
        ),
      },
    ],
  },
  'untagged-baggages-inventory': {
    title: title('Storage Inventory'),
    path: 'untagged-baggages/inventory',
    children: [
      {
        path: 'page/:page',
        url: (page: number) => `/untagged-baggages/inventory/page/${page}`,
        loadComponent: () => import('./untagged-baggages/components/untagged-baggages.component')
          .then((result) => result.UntaggedBaggagesComponent),
      },
      {
        path: ':id',
        url: (id: string) => `/untagged-baggages/inventory/${id}`,
        loadComponent: () => import('./untagged-baggages/components/details/untagged-baggage.component').then(
          (result) => result.UntaggedBaggageComponent,
        ),
      },
      {
        path: 'hot-search/:ubId',
        url: (id: string) => `/untagged-baggages/inventory/hot-search/${id}`,
        loadComponent: () => import('./hot-search/components/hot-search.component').then(
          (result) => result.HotSearchComponent,
        ),
      },
    ],
  },
  tablet: {
    title: title('Tablet'),
    path: 'tablet',
    url: () => '/tablet',
    loadComponent: () => import('./picture-acquisition/components/picture-acquisition.component')
      .then((result) => result.PictureAcquisitionComponent),
    canDeactivate: [confirmExitGuard],
  },
  'not-found': {
    title: title('Not Found'),
    path: '**',
    redirectTo: '/',
    pathMatch: 'full',
    url: () => '/',
  },
} as const;
